/**
 * Phase K.5 (2026-08-07) — PublicPayModal
 * Phase K.6 (2026-08-20) — save-bank opt-in checkbox
 *
 * Cloned from ``StatementPayModal.jsx`` but wired to the tokenized
 * public endpoints so the modal works without a JWT. The parent (the
 * public view page) passes:
 *   - ``kind``: "statement" | "invoice"
 *   - ``token``: HMAC-signed magic-link token from the URL
 *   - ``resource``: the loaded resource dict (used for display only)
 *   - ``amountCents``, ``label``, ``returnUrl`` for the Elements flow
 *   - ``hasActiveMandate``: bool from the token-fetch response (K.6)
 *
 * The pay endpoint is:
 *   POST /public/{kind}s/token/{token}/pay
 * which mints a Stripe PaymentIntent on the server side using the
 * customer_id embedded in the (verified) token — the browser never
 * receives payment authority beyond the standard Stripe client_secret.
 *
 * Save-bank toggle re-mints the PI (Stripe ``setup_future_usage``
 * is a PI-create parameter, not a confirm-time one) and remounts the
 * Elements provider via ``key={clientSecret}``.
 */
import React, { useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    PaymentElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle, Building2, Lock, ShieldCheck } from "lucide-react";
import publicApi from "@/lib/publicApi";
import { formatApiError } from "@/lib/api";

function fmtMoney(cents) {
    if (cents == null) return "—";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function PayInner({
    amountCents, label, returnUrl, onInitiated, closeModal,
    showSaveBank, saveBank, setSaveBank, saveBankRefetching,
}) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [elementReady, setElementReady] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || submitting || saveBankRefetching) return;
        setSubmitting(true);
        setError(null);
        try {
            onInitiated && onInitiated({ save_bank: saveBank });
            const result = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: returnUrl },
            });
            if (result?.error) {
                setError(
                    result.error.message ||
                        "We couldn't submit your bank authorization. Please try again.",
                );
            }
        } catch (err) {
            setError(err?.message || "Unexpected error submitting payment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="space-y-4"
            data-testid="public-pay-form"
        >
            <div className="rounded-lg border border-slate-200 bg-white p-3">
                <PaymentElement
                    options={{
                        layout: "tabs",
                        paymentMethodOrder: ["us_bank_account"],
                        wallets: { applePay: "never", googlePay: "never" },
                        fields: { billingDetails: { address: "never" } },
                    }}
                    onReady={() => setElementReady(true)}
                    onLoadError={(ev) =>
                        setError(
                            ev?.error?.message ||
                                "Stripe Elements failed to load. Refresh and try again.",
                        )
                    }
                />
            </div>

            {showSaveBank && (
                <label
                    className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none"
                    data-testid="public-pay-save-bank-row"
                >
                    <input
                        type="checkbox"
                        checked={saveBank}
                        onChange={(ev) => setSaveBank(ev.target.checked)}
                        disabled={submitting || saveBankRefetching}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-900 focus:ring-blue-500"
                        data-testid="public-pay-save-bank-checkbox"
                    />
                    <span className="leading-relaxed">
                        <strong>Save this bank account for one-click payments in the future.</strong>{" "}
                        Charges will happen automatically on future statements. Cancel anytime.
                        {" "}
                        <a
                            href="https://stripe.com/docs/ach#ach-nacha-authorization-agreement"
                            target="_blank" rel="noopener noreferrer"
                            className="underline text-blue-900"
                            data-testid="public-pay-save-bank-legal-link"
                        >
                            By checking this, you agree to the ACH authorization.
                        </a>
                        {saveBankRefetching && (
                            <span
                                className="ml-2 text-xs text-slate-500 inline-flex items-center gap-1"
                                data-testid="public-pay-save-bank-refetching"
                            >
                                <Loader2 size={11} className="animate-spin" />
                                updating…
                            </span>
                        )}
                    </span>
                </label>
            )}

            <div className="rounded-md bg-slate-50 ring-1 ring-slate-200 p-3 text-xs text-slate-600 leading-relaxed">
                <p className="flex items-start gap-2">
                    <ShieldCheck size={14} className="mt-0.5 text-emerald-700 shrink-0" />
                    <span>
                        By authorizing, you allow the vendor and its
                        payments partner Stripe to debit the bank account
                        selected for <strong>{fmtMoney(amountCents)}</strong>{" "}
                        against <strong>{label}</strong>. ACH debits typically
                        settle in 3–5 business days.
                    </span>
                </p>
            </div>

            {error && (
                <div
                    className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2"
                    data-testid="public-pay-error"
                >
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            <DialogFooter className="gap-2 pt-1">
                <Button
                    type="button"
                    variant="ghost"
                    onClick={closeModal}
                    disabled={submitting}
                    data-testid="public-pay-cancel"
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    disabled={!stripe || !elements || submitting || !elementReady || saveBankRefetching}
                    className="bg-blue-900 hover:bg-blue-800 gap-2 min-w-[160px]"
                    data-testid="public-pay-authorize"
                >
                    {submitting ? (
                        <>
                            <Loader2 size={14} className="animate-spin" /> Authorizing…
                        </>
                    ) : (
                        <>
                            <Lock size={14} /> Authorize {fmtMoney(amountCents)}
                        </>
                    )}
                </Button>
            </DialogFooter>
        </form>
    );
}


export default function PublicPayModal({
    kind, token, amountCents, label, returnUrl,
    open, onOpenChange, onInitiated,
    hasActiveMandate = false,
}) {
    const [boot, setBoot] = useState({
        loading: false, error: null,
        clientSecret: null, stripePromise: null,
    });
    const [saveBank, setSaveBank] = useState(false);
    const [saveBankRefetching, setSaveBankRefetching] = useState(false);
    // Track which (token, saveBank) combo we've already fetched so
    // toggling the checkbox back to a prior value doesn't re-fetch.
    const fetchedFor = useRef(null);

    const showSaveBank = !hasActiveMandate;

    useEffect(() => {
        if (!open || !token) return;
        const key = `${token}:${saveBank ? 1 : 0}`;
        if (fetchedFor.current === key) return;
        const isRefetch = fetchedFor.current !== null;
        fetchedFor.current = key;
        if (isRefetch) {
            setSaveBankRefetching(true);
        } else {
            setBoot({
                loading: true, error: null,
                clientSecret: null, stripePromise: null,
            });
        }
        (async () => {
            try {
                const path = kind === "invoice" ? "invoices" : "statements";
                const { data } = await publicApi.post(
                    `/public/${path}/token/${token}/pay`,
                    { save_bank: saveBank },
                );
                if (!data?.client_secret || !data?.publishable_key) {
                    throw new Error(
                        "Stripe payment session was not configured. Please try again shortly.",
                    );
                }
                const stripePromise = loadStripe(data.publishable_key);
                setBoot({
                    loading: false, error: null,
                    clientSecret: data.client_secret, stripePromise,
                });
            } catch (err) {
                setBoot({
                    loading: false, error: formatApiError(err),
                    clientSecret: null, stripePromise: null,
                });
            } finally {
                setSaveBankRefetching(false);
            }
        })();
    }, [open, token, kind, saveBank]);

    useEffect(() => {
        if (!open) {
            fetchedFor.current = null;
            setSaveBank(false);
            setSaveBankRefetching(false);
            setBoot({
                loading: false, error: null,
                clientSecret: null, stripePromise: null,
            });
        }
    }, [open]);

    const close = () => onOpenChange && onOpenChange(false);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[520px]"
                data-testid="public-pay-modal"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-xl text-slate-900 flex items-center gap-2">
                        <Building2 size={18} className="text-blue-900" />
                        Pay {fmtMoney(amountCents)} with bank account
                    </DialogTitle>
                    <DialogDescription className="text-slate-500">
                        {label} · ACH bank transfer. Settlement typically takes 3–5 business days.
                    </DialogDescription>
                </DialogHeader>

                {boot.loading && (
                    <div
                        className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"
                        data-testid="public-pay-loading"
                    >
                        <Loader2 size={14} className="animate-spin" />
                        Preparing secure bank-connect flow…
                    </div>
                )}

                {boot.error && (
                    <div
                        className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2"
                        data-testid="public-pay-boot-error"
                    >
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <span>{boot.error}</span>
                    </div>
                )}

                {boot.clientSecret && boot.stripePromise && (
                    <Elements
                        key={boot.clientSecret}
                        stripe={boot.stripePromise}
                        options={{
                            clientSecret: boot.clientSecret,
                            appearance: {
                                theme: "stripe",
                                variables: {
                                    colorPrimary: "#1e3a8a",
                                    colorText: "#0f172a",
                                    fontFamily:
                                        'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
                                    borderRadius: "8px",
                                },
                            },
                        }}
                    >
                        <PayInner
                            amountCents={amountCents}
                            label={label}
                            returnUrl={returnUrl}
                            onInitiated={onInitiated}
                            closeModal={close}
                            showSaveBank={showSaveBank}
                            saveBank={saveBank}
                            setSaveBank={setSaveBank}
                            saveBankRefetching={saveBankRefetching}
                        />
                    </Elements>
                )}
            </DialogContent>
        </Dialog>
    );
}
